import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useChat, useAppSelector, useAppDispatch } from '../hooks';
import { addOptimisticMessage } from '../store/slices';
import { ChatSidebar, ChatMessage, ChatInput, WorkoutPlanBuilder, NutritionPlanner } from '../components/chat';
import { SymptomChecker } from '../components/chat/SymptomChecker';
import { 
  chatService, 
  SymptomCategories, 
  SymptomAnalysisResult,
  NutritionPlannerMetadata,
  NutritionPlanResult,
  WeightGoal,
  Intensity,
  PlanDuration
} from '../services/chatService';
import { fetchProfile } from '../store/slices/profileSlice';
import './ChatPage.css';

export const ChatPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    conversations,
    currentConversation,
    isLoading,
    isSending,
    error,
    fetchConversations,
    startNewConversation,
    loadConversation,
    sendMessage,
    clearCurrentConversation,
  } = useChat();

  // Get user profile for photo
  const { profile } = useAppSelector((state) => state.profile);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSymptomCheckerOpen, setIsSymptomCheckerOpen] = useState(false);
  const [isWorkoutBuilderOpen, setIsWorkoutBuilderOpen] = useState(false);
  const [isNutritionPlannerOpen, setIsNutritionPlannerOpen] = useState(false);
  const [symptomCategories, setSymptomCategories] = useState<SymptomCategories | null>(null);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [nutritionMetadata, setNutritionMetadata] = useState<NutritionPlannerMetadata | null>(null);
  const [isNutritionMetadataLoading, setIsNutritionMetadataLoading] = useState(false);

  // Fetch profile on mount to get user's photo
  useEffect(() => {
    dispatch(fetchProfile());
  }, [dispatch]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentConversation?.messages]);

  // Fetch symptom categories when symptom checker opens
  useEffect(() => {
    if (isSymptomCheckerOpen && !symptomCategories) {
      setIsCategoriesLoading(true);
      chatService.getSymptomCategories()
        .then(categories => {
          setSymptomCategories(categories);
        })
        .catch(err => {
          console.error('Failed to load symptom categories:', err);
        })
        .finally(() => {
          setIsCategoriesLoading(false);
        });
    }
  }, [isSymptomCheckerOpen, symptomCategories]);

  // Fetch nutrition metadata when nutrition planner opens
  useEffect(() => {
    if (isNutritionPlannerOpen && !nutritionMetadata) {
      setIsNutritionMetadataLoading(true);
      chatService.getNutritionMetadata()
        .then(metadata => {
          setNutritionMetadata(metadata);
        })
        .catch(err => {
          console.error('Failed to load nutrition metadata:', err);
        })
        .finally(() => {
          setIsNutritionMetadataLoading(false);
        });
    }
  }, [isNutritionPlannerOpen, nutritionMetadata]);

  const handleNewConversation = async () => {
    await startNewConversation();
  };

  const handleSelectConversation = async (id: string) => {
    await loadConversation(id);
  };

  const handleSendMessage = async (message: string) => {
    if (currentConversation) {
      await sendMessage(currentConversation.conversationId, message);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleOpenSymptomChecker = () => {
    setIsSymptomCheckerOpen(true);
  };

  const handleCloseSymptomChecker = () => {
    setIsSymptomCheckerOpen(false);
  };

  const handleOpenWorkoutBuilder = () => {
    setIsWorkoutBuilderOpen(true);
  };

  const handleCloseWorkoutBuilder = () => {
    setIsWorkoutBuilderOpen(false);
  };

  const handleOpenNutritionPlanner = () => {
    setIsNutritionPlannerOpen(true);
  };

  const handleCloseNutritionPlanner = () => {
    setIsNutritionPlannerOpen(false);
  };

  const handleCreateNutritionPlan = useCallback(async (
    weightGoal: WeightGoal,
    intensity: Intensity,
    durationWeeks: PlanDuration
  ): Promise<NutritionPlanResult> => {
    return await chatService.createNutritionPlan(weightGoal, intensity, durationWeeks);
  }, []);

  const handleNutritionPlanDone = useCallback(async (result: NutritionPlanResult) => {
    // Create a summary message about the nutrition plan as system response
    const goalLabels = {
      weight_loss: 'Weight Loss',
      weight_gain: 'Weight Gain',
      maintain: 'Maintain Weight',
    };

    const intensityLabels = {
      gentle: 'Gentle (±100 kcal/week)',
      normal: 'Normal (±150-200 kcal/week)',
      aggressive: 'Aggressive (±250-450 kcal/week)',
    };

    // Build weekly plan summary
    const weeklyPlanSummary = result.weeklyPlan.map(week => 
      `**Week ${week.week}:** ${week.dailyCalories} kcal/day (${week.adjustmentFromMaintenance >= 0 ? '+' : ''}${week.adjustmentFromMaintenance} from maintenance)`
    ).join('\n');

    // Build pre-condition warnings if any
    const conditionWarnings = result.preConditionImpacts.length > 0
      ? `\n\n**Pre-Condition Adjustments:**\n${result.preConditionImpacts.map(c => `- **${c.condition}** (${c.icd10Code}): ${c.impact}`).join('\n')}`
      : '';

    // Build safety warnings if any
    const safetyWarnings = result.safetyWarnings.length > 0
      ? `\n\n**Important Considerations:**\n${result.safetyWarnings.map(w => `- ${w.message}`).join('\n')}`
      : '';

    const nutritionResponse = `🍽️ **Your Personalized Nutrition Plan**

**Profile Summary:**
- Age: ${result.profileSummary.age} years | Height: ${result.profileSummary.height} cm | Weight: ${result.profileSummary.weight} kg
- BMI: ${result.profileSummary.bmi} (${result.profileSummary.bmiCategory})
- Activity Level: ${result.profileSummary.activityLevel}

**Calorie Calculation:**
- BMR (Basal Metabolic Rate): ${result.maintenanceCalories.bmr} kcal/day
- TDEE (Maintenance Calories): ${result.maintenanceCalories.tdee} kcal/day

**Your Plan:**
- Goal: ${goalLabels[result.weightGoal]}
- Intensity: ${intensityLabels[result.intensity]}
- Duration: ${result.durationWeeks} weeks

**Weekly Calorie Targets:**
${weeklyPlanSummary}

**Expected Outcome:**
- Starting Weight: ${result.profileSummary.weight} kg
- Expected Final Weight: ${result.expectedOutcome.expectedFinalWeight} kg
- Expected Change: ${result.expectedOutcome.expectedWeightChange >= 0 ? '+' : ''}${result.expectedOutcome.expectedWeightChange.toFixed(1)} kg${conditionWarnings}${safetyWarnings}

---
*${result.disclaimer}*`;

    setIsNutritionPlannerOpen(false);

    // Ensure we have a conversation
    let convo = currentConversation;
    if (!convo) {
      convo = await startNewConversation();
    }

    // Add the nutrition plan as a system (assistant) response directly
    if (convo) {
      dispatch(addOptimisticMessage({
        role: 'assistant',
        content: nutritionResponse,
        timestamp: new Date().toISOString(),
      }));
    }
  }, [currentConversation, startNewConversation, dispatch]);

  const handleWorkoutGenerate = useCallback(async (message: string) => {
    // Close the workout builder
    setIsWorkoutBuilderOpen(false);

    // Send the workout plan request to chat
    if (currentConversation) {
      await sendMessage(currentConversation.conversationId, message);
    } else {
      // Create new conversation and then send
      const newConversation = await startNewConversation();
      if (newConversation) {
        await sendMessage(newConversation.conversationId, message);
      }
    }
  }, [currentConversation, sendMessage, startNewConversation]);

  const handleSymptomCheckerDoneWithMessage = useCallback(async (selectedSymptoms: string[], result: SymptomAnalysisResult) => {
    console.log('handleSymptomCheckerDoneWithMessage called', { selectedSymptoms, result });
    
    // Get symptom names from categories
    const symptomNames: string[] = [];
    if (symptomCategories) {
      selectedSymptoms.forEach(symptomId => {
        Object.values(symptomCategories).forEach(category => {
          const symptom = category.symptoms.find(s => s.id === symptomId);
          if (symptom) {
            symptomNames.push(symptom.name);
          }
        });
      });
    }

    // Create a message summarizing the symptom checker results
    const symptomMessage = `I just completed the Symptom Checker. Here are my selected symptoms: ${symptomNames.join(', ')}. 

The analysis showed:
- Severity: ${result.severity}
- Probable conditions: ${result.probableConditions.map(c => `${c.condition} (${c.probability} probability)`).join(', ')}

Based on these symptoms, can you provide more detailed guidance and recommendations considering my health profile?`;

    console.log('Symptom message created:', symptomMessage);
    console.log('Current conversation:', currentConversation);

    // If conversation exists, send immediately
    if (currentConversation) {
      // Refresh conversation to pull in any provider suggestions appended during analysis
      await loadConversation(currentConversation.conversationId);
      console.log('Sending to existing conversation:', currentConversation.conversationId);
      await sendMessage(currentConversation.conversationId, symptomMessage);
    } else {
      // Create new conversation and then send - use the returned conversation
      console.log('No conversation, creating new one...');
      const newConversation = await startNewConversation();
      console.log('New conversation created:', newConversation);
      if (newConversation) {
        // Not strictly necessary to reload here since it's a fresh convo
        await sendMessage(newConversation.conversationId, symptomMessage);
      }
    }
  }, [symptomCategories, currentConversation, sendMessage, startNewConversation, loadConversation]);

  const handleAnalyzeSymptoms = useCallback(async (symptoms: string[], pincode?: string): Promise<SymptomAnalysisResult> => {
    const convoId = currentConversation?.conversationId;
    return await chatService.analyzeSymptoms(symptoms, pincode, convoId);
  }, [currentConversation?.conversationId]);

  const handleDeleteConversations = useCallback(async (ids: string[]) => {
    try {
      // Delete each conversation individually
      for (const id of ids) {
        await chatService.deleteConversation(id);
      }
      // If current conversation was deleted, clear it
      if (currentConversation && ids.includes(currentConversation.conversationId)) {
        clearCurrentConversation();
      }
      await fetchConversations();
    } catch (err) {
      console.error('Failed to delete conversations:', err);
      throw err;
    }
  }, [fetchConversations, clearCurrentConversation, currentConversation]);

  const handleDeleteAllConversations = useCallback(async () => {
    try {
      await chatService.deleteAllConversations();
      clearCurrentConversation();
      await fetchConversations();
    } catch (err) {
      console.error('Failed to delete all conversations:', err);
      throw err;
    }
  }, [fetchConversations, clearCurrentConversation]);

  return (
    <div className="chat-page">
      <ChatSidebar
        conversations={conversations}
        currentConversationId={currentConversation?.conversationId}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onDeleteConversations={handleDeleteConversations}
        onDeleteAllConversations={handleDeleteAllConversations}
        isLoading={isLoading}
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
      />

      <main className="chat-main">
        {/* Mobile sidebar toggle */}
        <div className="chat-mobile-header">
          <button 
            className="mobile-sidebar-btn" 
            onClick={toggleSidebar}
            aria-label="Toggle chat history"
          >
            ☰
          </button>
          <span className="chat-mobile-title">ElevatePhysique AI</span>
        </div>

        {!currentConversation ? (
          <div className="chat-empty">
            <div className="chat-empty-content">
              <span className="chat-empty-icon">🏋️</span>
              <h2>Welcome to ElevatePhysique AI</h2>
              <p>Your personal health and wellness assistant</p>
              <button
                className="chat-empty-btn"
                onClick={handleNewConversation}
                disabled={isLoading}
              >
                Start New Conversation
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="chat-messages">
              {currentConversation.messages.length === 0 ? (
                <div className="chat-welcome">
                  <span className="chat-welcome-icon">👋</span>
                  <h3>How can I help you today?</h3>
                  <p>Ask me about heart health, diabetes, cancer, kidney, liver, vitamin deficiencies, skin conditions, viral fever, ayurvedic remedies, and nutrition!</p>
                  <div className="chat-suggestions">
                    <button
                      className="suggestion-btn"
                      onClick={() => handleSendMessage('What are the symptoms of Type 2 diabetes?')}
                    >
                      Diabetes symptoms
                    </button>
                    <button
                      className="suggestion-btn"
                      onClick={() => handleSendMessage('What causes skin rashes and how to treat them?')}
                    >
                      Skin conditions
                    </button>
                    <button
                      className="suggestion-btn"
                      onClick={() => handleSendMessage('What are ayurvedic remedies for digestive issues?')}
                    >
                      Ayurvedic remedies
                    </button>
                    <button
                      className="suggestion-btn"
                      onClick={() => handleSendMessage('What nutrition is recommended for ICD-10 conditions?')}
                    >
                      Nutrition guidance
                    </button>
                  </div>
                </div>
              ) : (
                currentConversation.messages.map((msg, index) => (
                  <ChatMessage 
                    key={index} 
                    message={msg} 
                    userPhotoUrl={profile?.profilePhotoUrl}
                    messageIndex={index}
                    conversationId={currentConversation.conversationId}
                  />
                ))
              )}
              {isSending && (
                <div className="chat-typing">
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                </div>
              )}
              {error && (
                <div className="chat-error">
                  {error}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <ChatInput
              onSend={handleSendMessage}
              disabled={!currentConversation}
              isLoading={isSending}
              onOpenSymptomChecker={handleOpenSymptomChecker}
              onOpenWorkoutBuilder={handleOpenWorkoutBuilder}
              onOpenNutritionPlanner={handleOpenNutritionPlanner}
            />
          </>
        )}
      </main>

      <SymptomChecker
        isOpen={isSymptomCheckerOpen}
        onClose={handleCloseSymptomChecker}
        onAnalyze={handleAnalyzeSymptoms}
        onDone={handleSymptomCheckerDoneWithMessage}
        categories={symptomCategories}
        isLoading={isCategoriesLoading}
      />

      <WorkoutPlanBuilder
        isOpen={isWorkoutBuilderOpen}
        onClose={handleCloseWorkoutBuilder}
        onGenerate={handleWorkoutGenerate}
      />

      <NutritionPlanner
        isOpen={isNutritionPlannerOpen}
        onClose={handleCloseNutritionPlanner}
        onCreatePlan={handleCreateNutritionPlan}
        onDone={handleNutritionPlanDone}
        metadata={nutritionMetadata}
        isLoading={isNutritionMetadataLoading}
      />
    </div>
  );
};
